import _ from 'lodash'
import {
  ColDef,
  ColGroupDef,
  GridApi,
  ProcessCellForExportParams,
  GridOptions,
} from 'ag-grid-community'
import { intl } from '../../../../i18n'
import { OrganizationWorkingDayCalendarDetail } from '../../../../lib/functions/organizationWorkingDayCalendar'
import { ProjectBasic, ProjectDetail } from '../../../../lib/functions/project'
import { UserDetail, UserProps } from '../../../../lib/functions/user'
import {
  DateTerm,
  generateWeekArray,
  generateMonthStartDateArray,
} from '../../../../utils/date'
import DateVO from '../../../../vo/DateVO'
import DateTimeVO from '../../../../vo/DateTimeVO'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import {
  ColumnType,
  columnTypes,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import {
  MemberWorkloadStatusType,
  MemberWorkloadStatusRow,
} from '../MemberWorkloadStatus'
import './styles.scss'
import {
  AUTO_GROUP_COLUMN_DEF_GROUP_ROW_BY_MEMBER,
  COLUMN_DEF_GROUP_ROW_BY_MEMBER,
  generateWorkMontColDefGroupRowByMember,
} from './columnDefGroupRowsByMemberCol'
import {
  WORKMONTHS_FIELD_PREFIX,
  WorkingDay,
  WorkingDayCalendar,
} from './commonColumnDef'

const INTERNAL_YEARMONTH_FORMAT: string = 'YYYY/MM'

export const memberWorkloadStatusGridOptions = (): GridOptions => {
  let autoGroupColumnDef: ColDef = {}
  let columnDefs: (ColDef | ColGroupDef)[] | null = []
  autoGroupColumnDef = AUTO_GROUP_COLUMN_DEF_GROUP_ROW_BY_MEMBER as ColDef
  columnDefs = COLUMN_DEF_GROUP_ROW_BY_MEMBER
  return {
    context: {},
    // Row
    treeData: true,
    excludeChildrenWhenTreeDataFiltering: true,
    rowDragManaged: false,
    rowDragMultiRow: false,
    suppressMoveWhenRowDragging: true,
    enterNavigatesVerticallyAfterEdit: true,
    autoGroupColumnDef,
    getRowStyle: params => {
      const row: MemberWorkloadStatusRow = params.data
      const rowBody = row?.body
      if (
        row &&
        row.type !== MemberWorkloadStatusType.SUMMARY &&
        rowBody &&
        !rowBody.validUser
      ) {
        return { backgroundColor: '#e0e0e0' }
      }
    },
    // Column
    columnTypes: columnTypes(),
    components: frameworkComponents,
    defaultColDef: {
      cellDataType: false,
      width: 100,
      editable: false,
      enableValue: false,
      hide: false,
      sortable: true,
      suppressHeaderMenuButton: true,
      suppressSizeToFit: false,
      singleClickEdit: false,
      resizable: true,
      cellEditor: 'textEditor',
      cellRenderer: DefaultCellRenderer,
      onCellClicked: defaultOnCellClicked,
      cellClassRules: {
        'grid-edited-cell': params => {
          const { colDef, data, value } = params
          const field = colDef.field ?? colDef.colId
          if (
            !data.editedData ||
            !field ||
            !colDef.editable ||
            (typeof colDef.editable === 'function' && !colDef.editable(params))
          ) {
            return false
          }
          return (
            data.editedData.hasOwnProperty(field) &&
            value !== data.editedData[field]
          )
        },
        member_workload_status_root_row: params => {
          const row: MemberWorkloadStatusRow = params.data
          return row.type === MemberWorkloadStatusType.MEMBER
        },
      },
    },
    columnDefs,
    excelStyles: [
      {
        id: 'numberStyle',
        dataType: 'Number',
        numberFormat: {
          format: '#,##0.00',
        },
      },
      {
        id: 'dateTimeStyle',
        dataType: 'DateTime',
        numberFormat: {
          format: 'yyyy/mm/dd hh:MM:ss',
        },
      },
    ],
    processCellForClipboard,
    processUnpinnedColumns: () => [],
  }
}

export const isWorkMonthColumn = (id: string | undefined) => {
  return !!id && id.startsWith(WORKMONTHS_FIELD_PREFIX)
}

export const isWorkMonthColumnDef = (def: ColDef | ColGroupDef) => {
  const field = (def as ColDef)?.field
  const groupId = (def as ColGroupDef)?.groupId
  return isWorkMonthColumn(field) || isWorkMonthColumn(groupId)
}

const filterWorkingDaysByDateRange = (
  workingDays: OrganizationWorkingDayCalendarDetail[],
  startDate: DateVO,
  endDate: DateVO
): OrganizationWorkingDayCalendarDetail[] => {
  const startValue = startDate.getValue()
  const endValue = endDate.getValue()
  return workingDays.filter(wd => {
    const wdDate = new DateVO(wd.date).getValue()
    return wdDate >= startValue && wdDate <= endValue
  })
}

const calculateWorkingTime = (
  workingDays: OrganizationWorkingDayCalendarDetail[]
): WorkingDay => {
  const workHours = workingDays.reduce((sum, wd) => sum + wd.workHour, 0)
  const businessDays = workingDays.filter(wd => wd.workHour > 0).length
  return { businessDays, workHours }
}

const getWorkingDayCalendars = (
  workingDays: OrganizationWorkingDayCalendarDetail[],
  dateKeys: string[],
  timeGrain: 'MONTH' | 'WEEK'
): WorkingDayCalendar => {
  const workingDayCalendars: WorkingDayCalendar = {}

  if (timeGrain === 'WEEK') {
    const uniqueMonths = new Set(
      dateKeys
        .map(key => key.match(/^(\d{4}\/\d{2})\/\d{2}$/)?.[1])
        .filter(Boolean) as string[]
    )

    uniqueMonths.forEach(yearMonth => {
      const monthStart = new DateVO(`${yearMonth}/01`)
      const monthEnd = monthStart.addMonths(1).addDays(-1)
      const monthWorkingDays = filterWorkingDaysByDateRange(
        workingDays,
        monthStart,
        monthEnd
      )
      workingDayCalendars[yearMonth] = calculateWorkingTime(monthWorkingDays)
    })
  }

  dateKeys.forEach(dateKey => {
    const startDate = new DateVO(dateKey)
    const endDate =
      timeGrain === 'WEEK'
        ? startDate.addDays(6)
        : startDate.addMonths(1).addDays(-1)
    const targetWorkingDays = filterWorkingDaysByDateRange(
      workingDays,
      startDate,
      endDate
    )
    workingDayCalendars[dateKey] = calculateWorkingTime(targetWorkingDays)
  })

  return workingDayCalendars
}

export type RefreshDynamicColumnDefProps = {
  api: GridApi | null | undefined
  dateTerm: DateTerm
  workingDays: OrganizationWorkingDayCalendarDetail[]
  timeGrain: 'MONTH' | 'WEEK'
}

export type RefreshDynamicColumnDefResult = {
  workingDayCalendars?: WorkingDayCalendar
}

const formatWorkingTimeTotal = (
  workingDayCalendars: WorkingDayCalendar | undefined,
  date: string
): string => {
  if (!workingDayCalendars) return '0d/0h'
  const target = workingDayCalendars[date]
  return target ? `${target.businessDays}d/${target.workHours}h` : '0d/0h'
}

const insertColumnDefs = (
  targetDefs: (ColDef | ColGroupDef)[] | null,
  newCols: ColGroupDef[],
  insertIndex: number | undefined
) => {
  newCols.forEach((col, index) => {
    if (insertIndex !== undefined) {
      targetDefs?.splice(insertIndex + index, 0, col)
    } else {
      targetDefs?.push(col)
    }
  })
}

const generateWeekColumnDefs = (
  dateArray: string[],
  generateWorkMonthColDef: (
    date: string,
    getWorkingTimeTotal: (date: string) => string
  ) => ColDef,
  workingDayCalendars: WorkingDayCalendar
): ColGroupDef[] => {
  const getWorkingTimeTotal = (date: string) =>
    formatWorkingTimeTotal(workingDayCalendars, date)

  const uniqueMonths = Array.from(
    new Set(
      dateArray.map(date => new DateVO(date).format(INTERNAL_YEARMONTH_FORMAT))
    )
  )

  const monthCols: ColGroupDef[] = uniqueMonths.map(yearMonth => {
    const date = new DateVO(yearMonth)
    return {
      groupId: `${WORKMONTHS_FIELD_PREFIX}${yearMonth}`,
      headerName: intl.formatMessage(
        { id: 'memberWorkload.status.yearMonth' },
        { year: date.getYear(), month: date.getMonth() }
      ),
      children: [],
    }
  })

  dateArray.forEach(weekStartDate => {
    const date = new DateVO(weekStartDate)
    const yearMonth = date.format(INTERNAL_YEARMONTH_FORMAT)
    const weekHeader: ColGroupDef = {
      headerName: intl.formatMessage(
        { id: 'memberWorkload.status.week' },
        { week: date.format('M/D') }
      ),
      groupId: `${WORKMONTHS_FIELD_PREFIX}${weekStartDate}`,
      children: [generateWorkMonthColDef(weekStartDate, getWorkingTimeTotal)],
    }

    const monthHeader = monthCols.find(
      col => col.groupId === `${WORKMONTHS_FIELD_PREFIX}${yearMonth}`
    )
    monthHeader?.children.push(weekHeader)
  })

  return monthCols
}

const generateMonthColumnDefs = (
  dateArray: string[],
  generateWorkMonthColDef: (
    date: string,
    getWorkingTimeTotal: (date: string) => string
  ) => ColDef,
  workingDayCalendars: WorkingDayCalendar
): ColGroupDef[] => {
  const getWorkingTimeTotal = (date: string) =>
    formatWorkingTimeTotal(workingDayCalendars, date)

  const uniqueYears = Array.from(
    new Set(dateArray.map(date => new DateVO(date).getYear()))
  )

  const yearCols: ColGroupDef[] = uniqueYears.map(year => ({
    groupId: `${WORKMONTHS_FIELD_PREFIX}${year}`,
    headerName: intl.formatMessage(
      { id: 'memberWorkload.status.year' },
      { year }
    ),
    children: [],
  }))

  dateArray.forEach(monthStartDate => {
    const date = new DateVO(monthStartDate)
    const monthHeader: ColGroupDef = {
      headerName: intl.formatMessage(
        { id: 'memberWorkload.status.month' },
        { month: date.getMonth() }
      ),
      groupId: `${WORKMONTHS_FIELD_PREFIX}${date.format(INTERNAL_YEARMONTH_FORMAT)}`,
      children: [generateWorkMonthColDef(monthStartDate, getWorkingTimeTotal)],
    }

    const yearHeader = yearCols.find(
      col => col.groupId === `${WORKMONTHS_FIELD_PREFIX}${date.getYear()}`
    )
    yearHeader?.children.push(monthHeader)
  })

  return yearCols
}

export const refreshDynamicColumnDef = async ({
  api,
  dateTerm,
  workingDays,
  timeGrain,
}: RefreshDynamicColumnDefProps): Promise<RefreshDynamicColumnDefResult> => {
  if (!api) return {}

  const generateWorkMonthColDef = generateWorkMontColDefGroupRowByMember
  const dateArray =
    timeGrain === 'WEEK'
      ? generateWeekArray(dateTerm)
      : generateMonthStartDateArray(dateTerm)

  const columnDefs = api.getColumnDefs()
  if (!columnDefs) return {}

  const workMonthColumnIndex = columnDefs.findIndex(isWorkMonthColumnDef)
  const newColDefs = columnDefs.filter(def => !isWorkMonthColumnDef(def))

  let workingDayCalendars: WorkingDayCalendar | undefined

  if (!_.isEmpty(dateArray)) {
    workingDayCalendars = getWorkingDayCalendars(
      workingDays,
      dateArray,
      timeGrain
    )

    const newCols =
      timeGrain === 'WEEK'
        ? generateWeekColumnDefs(
            dateArray,
            generateWorkMonthColDef,
            workingDayCalendars
          )
        : generateMonthColumnDefs(
            dateArray,
            generateWorkMonthColDef,
            workingDayCalendars
          )

    insertColumnDefs(newColDefs, newCols, workMonthColumnIndex)
  }

  api.setGridOption('columnDefs', newColDefs)
  return { workingDayCalendars }
}

export const processCellForClipboard = ({
  column,
  value,
}: ProcessCellForExportParams<MemberWorkloadStatusRow>) => {
  if (!value) return value

  const colDef = column.getColDef()
  const { field, type } = colDef

  if (type?.includes(ColumnType.autocomplete)) {
    return value?.name || value?.displayName || value?.officialName
  }
  if (type?.includes(ColumnType.dateTime)) {
    return new DateTimeVO(value).format()
  }
  if (field === 'body.project') {
    return projectCellValueFormatter(value)
  }
  if (field && ['body.user', 'body.project.projectManager'].includes(field)) {
    return getUserName(value)
  }
  return value
}

export const projectCellValueFormatter = (
  value: string | ProjectBasic | ProjectDetail | undefined
) => {
  if (!value) return ''
  if (typeof value === 'string') return value
  return (
    (value as ProjectDetail)?.displayName || (value as ProjectBasic)?.name || ''
  )
}

export const getUserName = (
  value: string | UserDetail | UserProps | undefined
): string => {
  if (!value) return ''
  return typeof value === 'string' ? value : value.name || ''
}
